import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, CookingPot, Bath, Shirt, Heart, Truck, ShieldCheck, Lock, ArrowRight } from 'lucide-react';
import { NotifyMeForm } from '../components/NotifyMeForm';

const CATEGORIES = [
  { name: 'Cleaning Essentials', icon: Sparkles, img: '/images/shop/eco-all-purpose-cleaner.jpg', span: 'row-span-2' },
  { name: 'Kitchen', icon: CookingPot, img: '/images/shop/oven-cleaning-kit.jpg' },
  { name: 'Bathroom', icon: Bath, img: '/images/shop/bathroom-descaler.jpg' },
  { name: 'Laundry', icon: Shirt, img: '/images/shop/laundry.jpg' },
  { name: 'Home Care', icon: Heart, img: '/images/shop/home-care.jpg' },
];

const PERKS = [
  { icon: ShieldCheck, title: 'Trusted by professionals', text: 'Every product is one our own cleaning team uses and recommends in Peterborough homes.' },
  { icon: Truck, title: 'Delivered to your door', text: 'Order online and have home essentials delivered straight to your doorstep.' },
  { icon: Lock, title: 'Secure card payments', text: 'Checkout will be powered by Stripe, so your card details never touch our servers.' },
];

export default function ShopComingSoon() {
  return (
    <div className="min-h-screen bg-white" data-testid="shop-coming-soon-page">
      <section className="relative overflow-hidden grain bg-gradient-to-br from-pink-50 via-white to-rose-50 pt-32 pb-20 lg:pb-28">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-pink-200/40 blur-3xl" aria-hidden="true" />
        <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-rose-100/60 blur-3xl" aria-hidden="true" />
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <span className="rise inline-flex items-center gap-2 rounded-full bg-gray-900 text-white text-xs font-semibold tracking-[0.2em] uppercase px-4 py-2" data-testid="coming-soon-badge">
                <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" /> Coming soon
              </span>
              <h1 className="rise rise-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.02] mt-6" data-testid="coming-soon-headline">
                THE CLEAN HOME<br />SHOP IS <span className="text-pink-600 italic">COMING</span>
              </h1>
              <p className="rise rise-2 text-base md:text-lg text-gray-600 mt-6 max-w-xl" data-testid="coming-soon-subheadline">
                Professional cleaning products. Home essentials. Everything you need for a cleaner home — delivered to your door.
              </p>
              <div className="rise rise-3 mt-10 max-w-lg">
                <p className="text-sm font-semibold text-gray-900 mb-3">Be the first to know when we launch.</p>
                <NotifyMeForm />
              </div>
            </div>

            <div className="lg:col-span-6 lg:pl-8">
              <div className="grid grid-cols-2 grid-rows-3 gap-4 h-[520px] sm:h-[600px]" data-testid="coming-soon-categories">
                {CATEGORIES.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.name} className={`rise rise-${i + 1} relative group rounded-3xl overflow-hidden shadow-lg shadow-pink-100/60 ${c.span || ''}`} data-testid={`category-tile-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/10 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 flex items-center gap-3 text-white">
                        <span className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center"><Icon className="w-4 h-4" /></span>
                        <span className="font-semibold text-sm sm:text-base">{c.name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-gray-500 mb-14" data-testid="category-strip">
            {CATEGORIES.map((c, i) => (
              <React.Fragment key={c.name}>
                {i > 0 && <span className="text-pink-400">•</span>}
                <span>{c.name}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {PERKS.map(p => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="relative pl-6 border-l-2 border-pink-200">
                  <Icon className="w-6 h-6 text-pink-600 mb-4" />
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-[2rem] bg-gray-900 text-white px-8 py-12 lg:px-16 lg:py-16 grain">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-pink-600/30 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div>
                <p className="text-pink-300 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Need a clean home now?</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">Our cleaning team is ready today.</h2>
                <p className="text-gray-300 mt-3 max-w-xl">Regular, deep, end-of-tenancy and commercial cleaning across Peterborough. Get a free quote in under two minutes.</p>
              </div>
              <Link to="/#contact" data-testid="coming-soon-quote-cta" className="inline-flex items-center gap-2 self-start lg:self-auto rounded-full bg-pink-500 hover:bg-pink-400 text-white font-semibold px-7 py-4 transition-colors">
                Request a free quote <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
